import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '../../../services/api';
import {
  isMonthly, isCarryOverLocked, canUseCarryOver, normalizeAccrualFields, legalFrameworkOf,
  MAX_ELIGIBILITY_MONTHS, maxExpiryMonthsFor, LABOR_4857, CIVIL_SERVANT_657,
} from '../../../utils/leaveAccrual';

const BoolBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $val }) => $val ? '#2e7d3215' : '#9e9e9e15'};
  color: ${({ $val }) => $val ? '#2e7d32' : '#9e9e9e'};
`;

const GenderBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: #0291ef15;
  color: #0291ef;
`;

const BALANCE_SCOPE_COLORS = { annual: '#2e7d32', own: '#0291ef', none: '#9e9e9e' };
const CATEGORY_UNCLASSIFIED_COLOR = '#f57c00';
const CATEGORY_COLOR = '#00897b';

const ScopeBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $color }) => `${$color}15`};
  color: ${({ $color }) => $color};
`;

// Kategori "other" (sınıflandırılmamış) kaldığında farklı bir renkte UYARI
// rozeti gösterir — geri doldurma mevcut türlerin çoğunu 'other' bıraktı,
// İK'nın bunları sınıflandırması gerekiyor (Task 10 brief).
const CategoryBadge = ({ value, label }) => (
  <ScopeBadge $color={value === 'other' ? CATEGORY_UNCLASSIFIED_COLOR : CATEGORY_COLOR}>
    {label}
  </ScopeBadge>
);

// getFormSchema saf bir fonksiyondur — testte doğrudan import edilip `t`
// olarak kimlik fonksiyonu (`k => k`) geçirilerek alan tanımları
// (type/required/options/hidden) hook'u hiç render etmeden denetlenebilir.
// `ctx.countryCode`: kiracının ülkesi (GET /v1/leave-variations/accrual-context) —
// Türkiye'de yasal yıllık izne devir tavanı alanları gizlenir (backend ayrıca reddeder).
// İstek gövdesi: hak ediş alanları normalize edilir; yasal çerçeve SAYI gider (boş → 4857). Boş/null
// enum alanı System.Text.Json'da tüm gövdeyi reddettirirdi.
export const toLeaveVariationPayload = (formData, countryCode) => ({
  ...normalizeAccrualFields(formData, countryCode),
  legalFramework: legalFrameworkOf(formData),
});

export const getFormSchema = (t, ctx = {}) => [
  { name: 'name', label: t('leaveVariation.name'), type: 'text', grid: 12, required: true },
  { name: 'description', label: t('leaveVariation.description'), type: 'textarea', grid: 12 },
  // AKM POC §6.4 — yasal çerçeve: 657 türünde yıllık izne devir tavanı/süresi KİLİTLİ DEĞİLDİR.
  {
    name: 'legalFramework', label: t('leaveVariation.legalFramework'), type: 'select', grid: 12,
    required: true, defaultValue: LABOR_4857,
    options: [
      { id: LABOR_4857, name: t('leaveVariation.labor4857') },
      { id: CIVIL_SERVANT_657, name: t('leaveVariation.civilServant657') },
    ],
    idKey: 'id', labelKey: 'name',
  },
  {
    name: 'forGender', label: t('leaveVariation.forGender'), type: 'select', grid: 12,
    options: [
      { id: 'all', name: t('leaveVariation.genderAll') },
      { id: 'male', name: t('leaveVariation.genderMale') },
      { id: 'female', name: t('leaveVariation.genderFemale') },
    ],
    idKey: 'id', labelKey: 'name',
  },
  { name: 'instanceLimit', label: t('leaveVariation.instanceLimit'), type: 'number', grid: 6 },
  { name: 'monthlyLimit', label: t('leaveVariation.monthlyLimit'), type: 'number', grid: 6 },
  { name: 'negativeBalanceLimit', label: t('leaveVariation.negativeBalanceLimit'), type: 'number', grid: 6 },
  { name: 'cooldownDays', label: t('leaveVariation.cooldownDays'), type: 'number', grid: 6 },
  { name: 'isPaid', label: t('leaveVariation.isPaid'), type: 'checkbox', grid: 6 },
  // --- Bakiye ve sınıf ekseni (bkz. backend/Flexbee.Api/Common/LeaveKinds.cs) ---
  // "Mazeret izni" gibi tek bir onay kutusu YOK: balanceScope üç durumlu
  // (annual/own/none) ve ikili bir bayrakla ifade edilemez; category de ayrı
  // bir eksen (bordro/rapor sınıfı). İkisi de SELECT ve required — backend
  // LeaveAxisValidator ikisini de zorunlu kılıyor (categoryRequired,
  // invalidBalanceScope/invalidCategory).
  {
    name: 'balanceScope', label: t('leaveVariation.balanceScope'), type: 'select', grid: 6,
    required: true,
    options: [
      { id: 'annual', name: t('leaveVariation.balanceAnnual') },
      { id: 'own', name: t('leaveVariation.balanceOwn') },
      { id: 'none', name: t('leaveVariation.balanceNone') },
    ],
  },
  {
    name: 'category', label: t('leaveVariation.category'), type: 'select', grid: 6,
    required: true,
    options: [
      { id: 'annual', name: t('leaveVariation.categoryAnnual') },
      { id: 'statutory', name: t('leaveVariation.categoryStatutory') },
      { id: 'medical', name: t('leaveVariation.categoryMedical') },
      { id: 'maternity', name: t('leaveVariation.categoryMaternity') },
      { id: 'benefit', name: t('leaveVariation.categoryBenefit') },
      { id: 'unpaid', name: t('leaveVariation.categoryUnpaid') },
    ],
  },
  {
    name: 'entitlementBasis', label: t('leaveVariation.entitlementBasis'), type: 'select', grid: 6,
    options: [
      { id: 'perYear', name: t('leaveVariation.basisPerYear') },
      { id: 'perEvent', name: t('leaveVariation.basisPerEvent') },
    ],
  },
  {
    name: 'eligibilityAnchor', label: t('leaveVariation.eligibilityAnchor'), type: 'select', grid: 6,
    options: [
      { id: 'none', name: t('leaveVariation.anchorNone') },
      { id: 'birthday', name: t('leaveVariation.anchorBirthday') },
    ],
    // İnceleme turu (Task 10 düzeltmesi) — Important bulgu: kullanıcı
    // 'birthday' seçip anchorToleranceDays'e değer girdikten sonra çapayı
    // 'none'a çevirirse alan formdan GİZLENİR (yukarıdaki hidden) ama eski
    // değer formData'da yaşamaya devam edip backend'e gidiyordu.
    // LeaveAxisValidator toleransı yalnız çapa 'birthday' iken kontrol
    // ettiği için 400 dönmüyor, anlamsız değer sessizce kaydediliyordu.
    // GenericFormDrawer'ın deriveFields kancası (handleFieldChange) tam bu
    // iş için var — çapa birthday DIŞINA çevrildiğinde toleransı temizler;
    // birthday'e dönüşte dokunmaz (undefined → yama yok), kullanıcı az önce
    // sildiği bir değeri geri yazmak zorunda kalmasın diye.
    derives: ['anchorToleranceDays'],
    deriveFields: (value) => (value !== 'birthday' ? { anchorToleranceDays: null } : undefined),
  },
  {
    name: 'anchorToleranceDays', label: t('leaveVariation.anchorToleranceDays'),
    type: 'number', grid: 6,
    hidden: (formData) => formData.eligibilityAnchor !== 'birthday',
  },
  { name: 'requiresDocument', label: t('leaveVariation.requiresDocument'), type: 'checkbox', grid: 6 },
  { name: 'payrollCode', label: t('leaveVariation.payrollCode'), type: 'text', grid: 6 },
  { name: 'isHourly', label: t('leaveVariation.isHourly'), type: 'checkbox', grid: 6 },
  { name: 'fridayPlusOne', label: t('leaveVariation.fridayPlusOne'), type: 'checkbox', grid: 6 },
  { name: 'carriesForward', label: t('leaveVariation.carriesForward'), type: 'checkbox', grid: 6 },
  { name: 'fullWeekPlusOne', label: t('leaveVariation.fullWeekPlusOne'), type: 'checkbox', grid: 6 },
  // --- Hak ediş biçimi (bkz. backend Common/LeaveAccrualEngine.cs) ---
  // Yıllık: tamamlanan her hizmet yılında toplu hak ediş (Türkiye modeli, varsayılan).
  // Aylık: yıllık hakkın 1/12'si her tamamlanan hizmet ayında birikir (ör. BAE).
  {
    name: 'accrualFrequency', label: t('leaveVariation.accrualFrequency'), type: 'select', grid: 6,
    defaultValue: 'yearly',
    options: [
      { id: 'yearly', name: t('leaveVariation.accrualYearly') },
      { id: 'monthly', name: t('leaveVariation.accrualMonthly') },
    ],
    // Yıllığa dönüşte aylık-özel ayarlar varsayılana iner — gizli alanda eski değer
    // kalıp gövdeye gitmesin (anchorToleranceDays ile aynı gerekçe).
    derives: ['accrualEligibilityMonths', 'accrualTopUpAtAnniversary', 'accrualRounding'],
    deriveFields: (value) => (value !== 'monthly'
      ? { accrualEligibilityMonths: 0, accrualTopUpAtAnniversary: false, accrualRounding: 'exact' }
      : undefined),
  },
  {
    name: 'accrualRounding', label: t('leaveVariation.accrualRounding'), type: 'select', grid: 6,
    defaultValue: 'exact',
    options: [
      { id: 'exact', name: t('leaveVariation.roundingExact') },
      { id: 'halfDown', name: t('leaveVariation.roundingHalfDown') },
    ],
    hidden: (fd) => !isMonthly(fd),
  },
  {
    name: 'accrualEligibilityMonths', label: t('leaveVariation.accrualEligibilityMonths'), type: 'number', grid: 6,
    defaultValue: 0, min: 0, max: MAX_ELIGIBILITY_MONTHS, step: 1,
    hidden: (fd) => !isMonthly(fd),
  },
  {
    name: 'accrualTopUpAtAnniversary', label: t('leaveVariation.accrualTopUpAtAnniversary'), type: 'checkbox', grid: 6,
    hidden: (fd) => !isMonthly(fd),
  },
  {
    name: 'monthlyAccrualHint', type: 'notice', grid: 12,
    content: t('leaveVariation.monthlyAccrualHint'),
    hidden: (fd) => !isMonthly(fd),
  },
  // --- Devir tavanı: yalnız devreden türde; Türkiye'de yasal yıllık izne KİLİTLİ ---
  {
    name: 'carryOverCap', label: t('leaveVariation.carryOverCap'), type: 'number', grid: 6,
    min: 0, step: 0.5,
    hidden: (fd) => !canUseCarryOver(fd, ctx.countryCode),
  },
  {
    name: 'carryOverExpiryMonths', label: t('leaveVariation.carryOverExpiryMonths'), type: 'number', grid: 6,
    min: 1, max: (fd) => maxExpiryMonthsFor(fd), step: 1,
    hidden: (fd) => !canUseCarryOver(fd, ctx.countryCode),
  },
  {
    name: 'carryOverHint', type: 'notice', grid: 12,
    content: t('leaveVariation.carryOverHint'),
    hidden: (fd) => !canUseCarryOver(fd, ctx.countryCode),
  },
  {
    name: 'carryOverLockedTurkey', type: 'notice', grid: 12, tone: 'warning',
    content: t('leaveVariation.carryOverLockedTurkey'),
    hidden: (fd) => !(fd.carriesForward && isCarryOverLocked(fd, ctx.countryCode)),
  },
  { name: 'entitlementRules', label: t('leaveVariation.entitlementRules'), type: 'entitlementRulesEditor', grid: 12 },
];

export const useLeaveVariationApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);
  // Kiracının ülkesi — Türkiye kilidi için. Okunamazsa Türkiye kabul edilir (güvenli taraf).
  const [accrualContext, setAccrualContext] = useState({ countryCode: 'TR' });

  useEffect(() => {
    api.get('/v1/leave-variations/accrual-context')
      .then((res) => {
        if (res.data?.status && res.data.data) setAccrualContext(res.data.data);
      });
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get('/v1/leave-variations')
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: items.length,
          pageIndex: 0,
          pageSize: items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/leave-variations', toLeaveVariationPayload(formData, accrualContext.countryCode));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, [accrualContext]);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/leave-variations/${id}`, toLeaveVariationPayload(formData, accrualContext.countryCode));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, [accrualContext]);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/leave-variations/${ids[0]}`);
      } else {
        await api.post('/v1/leave-variations/batch-delete', ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const genderLabels = useMemo(() => ({
    all: t('leaveVariation.genderAll'),
    male: t('leaveVariation.genderMale'),
    female: t('leaveVariation.genderFemale'),
  }), [t]);

  const balanceLabels = useMemo(() => ({
    annual: t('leaveVariation.balanceAnnual'),
    own: t('leaveVariation.balanceOwn'),
    none: t('leaveVariation.balanceNone'),
  }), [t]);

  const categoryLabels = useMemo(() => ({
    annual: t('leaveVariation.categoryAnnual'),
    statutory: t('leaveVariation.categoryStatutory'),
    medical: t('leaveVariation.categoryMedical'),
    maternity: t('leaveVariation.categoryMaternity'),
    benefit: t('leaveVariation.categoryBenefit'),
    unpaid: t('leaveVariation.categoryUnpaid'),
    other: t('leaveVariation.categoryUnclassified'),
  }), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('leaveVariation.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'forGender', label: t('leaveVariation.forGender'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['all', 'male', 'female'],
          renderValue: (v) => genderLabels[v] || v,
        },
        customFilterListOptions: { render: (v) => genderLabels[v] || v },
        customBodyRender: (v) => <GenderBadge>{genderLabels[v] || v}</GenderBadge>,
      },
    },
    {
      name: 'balanceScope', label: t('leaveVariation.balanceScope'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['annual', 'own', 'none'],
          renderValue: (v) => balanceLabels[v] || v,
        },
        customFilterListOptions: { render: (v) => balanceLabels[v] || v },
        customBodyRender: (v) => <ScopeBadge $color={BALANCE_SCOPE_COLORS[v] || BALANCE_SCOPE_COLORS.none}>{balanceLabels[v] || v}</ScopeBadge>,
      },
    },
    {
      name: 'category', label: t('leaveVariation.category'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['annual', 'statutory', 'medical', 'maternity', 'benefit', 'unpaid', 'other'],
          renderValue: (v) => categoryLabels[v] || v,
        },
        customFilterListOptions: { render: (v) => categoryLabels[v] || v },
        customBodyRender: (v) => <CategoryBadge value={v} label={categoryLabels[v] || v} />,
      },
    },
    {
      name: 'accrualFrequency', label: t('leaveVariation.accrualFrequency'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (
          <ScopeBadge $color={v === 'monthly' ? '#6a1b9a' : BALANCE_SCOPE_COLORS.none}>
            {v === 'monthly' ? t('leaveVariation.accrualMonthly') : t('leaveVariation.accrualYearly')}
          </ScopeBadge>
        ),
      },
    },
    {
      name: 'isPaid', label: t('leaveVariation.isPaid'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['true', 'false'],
          renderValue: (v) => v === 'true' ? t('common.yes') : t('common.no'),
        },
        customFilterListOptions: { render: (v) => v === 'true' ? t('common.yes') : t('common.no') },
        customBodyRender: (v) => <BoolBadge $val={v}>{v ? t('common.yes') : t('common.no')}</BoolBadge>,
      },
    },
    {
      name: 'isHourly', label: t('leaveVariation.isHourly'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['true', 'false'],
          renderValue: (v) => v === 'true' ? t('common.yes') : t('common.no'),
        },
        customFilterListOptions: { render: (v) => v === 'true' ? t('common.yes') : t('common.no') },
        customBodyRender: (v) => <BoolBadge $val={v}>{v ? t('common.yes') : t('common.no')}</BoolBadge>,
      },
    },
  ], [t, genderLabels, balanceLabels, categoryLabels]);

  const objectSchema = useMemo(() => getFormSchema(t, accrualContext), [t, accrualContext]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};
